/**
 * FirmClaim — 4-step wizard at `/claim/:slug`.
 *
 *   Step 1 · Auth check   — must be signed in with Google.
 *   Step 2 · Work email   — collect firm work email + issue 6-digit code
 *                            (returned in `verification_code_debug` while
 *                            email delivery is stubbed).
 *   Step 3 · Verify code  — enter the 6-digit code; auto-approves if the
 *                            work email domain matches `verified_domains`
 *                            on the firm doc, otherwise queues admin review.
 *   Step 4 · Done         — success screen with a link to the Firm Portal
 *                            (approved) or a "we'll email you when reviewed"
 *                            note (pending admin).
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, LogIn, Mail, ShieldCheck, ArrowRight } from "lucide-react";

const STEPS = ["auth", "email", "verify", "done"];

export default function FirmClaim() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [firm, setFirm] = useState(null);
  const [step, setStep] = useState("auth");
  const [workEmail, setWorkEmail] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [claim, setClaim] = useState(null); // { claim_id, verification_code_debug, expires_in_seconds }
  const [code, setCode] = useState("");
  const [finalStatus, setFinalStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Fetch firm meta so we can show the firm name in the header.
    // The endpoint reads the session cookie (via optional auth) and
    // returns `is_owner: true` when the signed-in caller already
    // has a verified claim on this firm — in that case skip the
    // wizard entirely.
    api.get(`/firm-claims/status/${slug}`)
      .then((r) => {
        setFirm(r.data);
        if (r.data?.is_owner) {
          toast.success("You already own this firm — opening your portal");
          navigate(`/firm-portal/${slug}`, { replace: true });
        }
      })
      .catch(() => setFirm(null));
  }, [slug, navigate, user?.user_id]);

  useEffect(() => {
    // Only auto-advance from AUTH to EMAIL for users who are NOT
    // already owners — the owner path is handled by the redirect in
    // the status-fetch effect above.
    if (user && step === "auth" && firm && !firm.is_owner) {
      setStep("email");
    }
  }, [user, step, firm]);

  const submitClaim = async () => {
    setBusy(true);
    try {
      const r = await api.post("/firm-claims", {
        firm_slug: slug,
        work_email: workEmail.trim(),
        job_title: jobTitle.trim() || null,
        linkedin_url: linkedinUrl.trim() || null,
      });
      setClaim(r.data);
      setStep("verify");
      toast.success("Verification code issued");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the claim");
    }
    setBusy(false);
  };

  const verifyCode = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/firm-claims/${claim.claim_id}/verify-code`, { code: code.trim() });
      setFinalStatus(r.data.status);
      setStep("done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid or expired code");
    }
    setBusy(false);
  };

  return (
    <main className="max-w-2xl mx-auto px-6 py-10 space-y-8" data-testid="firm-claim-root">
      <header>
        <div className="text-[10px] font-underground-sc tracking-[0.18em] text-stone-500 mb-1">
          Claim firm profile
        </div>
        <h1 className="text-3xl font-editorial text-stone-900">
          Claim {firm?.firm_slug ? <span className="text-[#4C1D95]">{slug}</span> : "your firm's profile"}
        </h1>
        <p className="text-stone-600 mt-2 text-sm leading-relaxed">
          Once approved you'll be able to edit the firm's blurb, logo, CTA link,
          and D&I stats. Every edit goes through our editorial team before
          publishing — no ranking or feeder data can be edited.
        </p>
        {firm?.is_claimed && (
          <div
            data-testid="firm-claim-already-claimed"
            className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-900 text-sm flex items-center gap-2"
          >
            <CheckCircle2 className="h-4 w-4" />
            This firm has already been claimed by a verified team member.
          </div>
        )}
      </header>

      <StepIndicator step={step} />

      {step === "auth" && (
        <section className="rounded-xl border border-stone-200 bg-white p-6 space-y-4" data-testid="firm-claim-step-auth">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <LogIn className="h-4 w-4 text-[#002147]" /> Sign in first
          </h2>
          <p className="text-sm text-stone-600">
            We need a signed-in account to attach the claim to. Any Google
            account works — we'll verify your firm identity on the next step.
          </p>
          <Button
            data-testid="firm-claim-signin-btn"
            onClick={login}
            className="bg-[#002147] text-white hover:bg-[#001A38]"
          >
            <LogIn className="h-4 w-4 mr-1.5" /> Sign in
          </Button>
        </section>
      )}

      {step === "email" && (
        <section className="rounded-xl border border-stone-200 bg-white p-6 space-y-4" data-testid="firm-claim-step-email">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Mail className="h-4 w-4 text-[#002147]" /> Work email
          </h2>
          <p className="text-sm text-stone-600">
            Enter the email address you use at the firm (e.g. you@firm.com).
            Public providers (gmail, outlook, yahoo) can't be used to verify
            firm ownership.
          </p>
          <div className="space-y-2">
            <label className="text-xs font-medium text-stone-700">Work email</label>
            <Input
              data-testid="firm-claim-work-email"
              type="email"
              value={workEmail}
              onChange={(e) => setWorkEmail(e.target.value)}
              placeholder="you@yourfirm.com"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-xs font-medium text-stone-700">Job title (optional)</label>
              <Input
                data-testid="firm-claim-job-title"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="Head of Early Careers"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-stone-700">LinkedIn (optional)</label>
              <Input
                data-testid="firm-claim-linkedin"
                value={linkedinUrl}
                onChange={(e) => setLinkedinUrl(e.target.value)}
                placeholder="https://linkedin.com/in/…"
              />
            </div>
          </div>
          <Button
            data-testid="firm-claim-request-code-btn"
            onClick={submitClaim}
            disabled={busy || !workEmail.trim()}
            className="bg-[#4C1D95] text-white hover:bg-[#3B0764]"
          >
            {busy ? "Requesting…" : "Send verification code"} <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </section>
      )}

      {step === "verify" && (
        <section className="rounded-xl border border-stone-200 bg-white p-6 space-y-4" data-testid="firm-claim-step-verify">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-[#002147]" /> Verify code
          </h2>
          <p className="text-sm text-stone-600">
            We sent a 6-digit code to <strong>{workEmail}</strong>. Enter it below.
            The code expires in 30 minutes.
          </p>
          {claim?.verification_code_debug && (
            <div
              data-testid="firm-claim-debug-code"
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-mono text-amber-900"
            >
              <strong>DEV MODE:</strong> Email delivery is stubbed — use code {" "}
              <span className="text-base tracking-[0.4em] font-bold">{claim.verification_code_debug}</span>
            </div>
          )}
          <div className="space-y-2 max-w-[220px]">
            <label className="text-xs font-medium text-stone-700">6-digit code</label>
            <Input
              data-testid="firm-claim-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              className="tracking-[0.4em] font-mono text-center text-lg"
            />
          </div>
          <Button
            data-testid="firm-claim-verify-btn"
            onClick={verifyCode}
            disabled={busy || code.length !== 6}
            className="bg-[#4C1D95] text-white hover:bg-[#3B0764]"
          >
            {busy ? "Verifying…" : "Verify code"}
          </Button>
        </section>
      )}

      {step === "done" && (
        <section className="rounded-xl border border-stone-200 bg-white p-6 space-y-4" data-testid="firm-claim-step-done">
          {finalStatus === "approved" ? (
            <>
              <div className="flex items-center gap-2 text-emerald-800">
                <CheckCircle2 className="h-5 w-5" />
                <h2 className="text-lg font-semibold">You're verified.</h2>
              </div>
              <p className="text-sm text-stone-600">
                Your firm domain matched an approved allowlist — you're in.
                Head to the Firm Portal to start editing your profile.
              </p>
              <Button
                data-testid="firm-claim-open-portal-btn"
                onClick={() => navigate(`/firm-portal/${slug}`)}
                className="bg-[#4C1D95] text-white hover:bg-[#3B0764]"
              >
                Open Firm Portal <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 text-amber-800">
                <ShieldCheck className="h-5 w-5" />
                <h2 className="text-lg font-semibold">Under review</h2>
              </div>
              <p className="text-sm text-stone-600">
                Thanks — we've received your claim. Our editorial team reviews
                submissions within 3-5 working days. You'll receive an email at{" "}
                <strong>{workEmail}</strong> once the decision is in.
              </p>
              <Link
                data-testid="firm-claim-back-to-firm-link"
                to={`/how-to-get-into/${slug}`}
                className="text-sm text-[#4C1D95] hover:text-[#3B0764] font-medium inline-flex items-center gap-1"
              >
                ← Back to firm page
              </Link>
            </>
          )}
        </section>
      )}
    </main>
  );
}

function StepIndicator({ step }) {
  const idx = STEPS.indexOf(step);
  return (
    <ol className="flex items-center gap-2 text-[10px] font-underground-sc tracking-[0.18em]" data-testid="firm-claim-step-indicator">
      {STEPS.map((s, i) => (
        <li
          key={s}
          className={
            "px-2 py-1 rounded-full border " +
            (i < idx
              ? "border-emerald-300 bg-emerald-50 text-emerald-900"
              : i === idx
              ? "border-[#4C1D95] bg-[#F5F0FF] text-[#4C1D95] font-semibold"
              : "border-stone-200 bg-white text-stone-400")
          }
        >
          {s}
        </li>
      ))}
    </ol>
  );
}
